// frontend/src/services/storageService.js
// Photo storage and processing operations
import { supabase } from './supabaseCore';

const BUCKET = 'player-photos';

/**
 * Copy a Sportradar photo into our own storage, straight from the browser.
 *
 * The get-sportradar-photo Edge Function does the same job but costs three Sportradar
 * requests per player — an OAuth token, an images lookup, then the download — which is
 * 126 requests for a 42-player squad against an API whose quota we do not know.
 *
 * The image URL is already in the mirror, and Sportradar's asset host answers with
 * `Access-Control-Allow-Origin: *` and a one-year cache header, so the browser can fetch
 * it directly. That leaves their API untouched: only a static CDN is hit, once per photo.
 *
 * `?size=RAW` matters — the bare URL serves 100x100 while RAW gives the 130x130 original,
 * which is the largest Sportradar holds.
 *
 * The asset host must be listed in `connect-src` in vercel.json. `img-src` already allows
 * `https:` so thumbnails render regardless, but fetch() is governed by connect-src, and a
 * missing entry fails only in production — the dev server sends no CSP at all.
 *
 * Returns the public URL of our copy. We keep a copy rather than storing their URL so a
 * roster photo cannot disappear later, and so exports never depend on a third party.
 */
export const copySportradarPhoto = async ({ imageUrl, eventCode, teamName, playerName }) => {
    if (!imageUrl) return { url: null, error: new Error('No Sportradar image for this person') };

    try {
        const source = imageUrl.includes('?') ? imageUrl : `${imageUrl}?size=RAW&format=webp`;
        const response = await fetch(source);
        if (!response.ok) throw new Error(`Sportradar returned ${response.status}`);

        const blob = await response.blob();
        const extension = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const safeName = (playerName || 'player').replace(/\s+/g, '_').replace(/[^\w-]/g, '');
        const path = `${eventCode}/${teamName}/${safeName}_SR_${Date.now()}.${extension}`;

        const { error: uploadError } = await supabase.storage
            .from(BUCKET)
            .upload(path, blob, { contentType: blob.type, upsert: true });
        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
        return { url: data.publicUrl, error: null };
    } catch (error) {
        console.error('Copying Sportradar photo failed:', error);
        return { url: null, error };
    }
};

// Remove background using Edge Function (API key stored server-side)
export const removeBackground = async (photoUrl) => {
    try {
        const { data: result, error } = await supabase.functions.invoke('remove-background', {
            body: { photoUrl }
        });

        if (error) {
            throw new Error(error.message || 'Background removal request failed');
        }

        // Convert base64 back to blob (chunk-safe method to avoid stack overflow)
        const binaryString = atob(result.imageBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: result.contentType || 'image/png' });

    } catch (error) {
        console.error('Background removal failed:', error);
        throw error;
    }
};
